import { Component, Input, OnInit, OnDestroy, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { FieldsConfigService } from 'src/app/tabms/services/global/fields-config.service';
import { FieldDictionaryDto } from 'src/app/tabms/models/base/base';
import { BaseService } from 'src/app/tabms/services/base/base.service';
import { ThemeLayoutService } from 'src/app/@theme/services/theme-layout.service';
import { RTL } from 'src/app/@theme/const';
import { FieldDictionaryListService } from 'src/app/tabms/services/global/field-dictionary-list.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BaseFieldConfigComponent } from '../base-field-config/base-field-config.component';

@Component({
  selector: 'app-base-form-label',
  templateUrl: './base-form-label.component.html',
  styleUrls: ['./base-form-label.component.scss']
})
export class BaseFormLabelComponent<T> implements OnInit, OnDestroy {
  @Input() set fieldNameInput(_fieldName) {
    //field name as it is in db table and FieldDictionary table
    if (!_fieldName) return;
    this.fieldName = _fieldName;
  }

  @Input() set showLabel(_showLabel) {
    //true if we need to show the label and false if we don't need to show the label just show config icon for example with checkbox
    if (!_showLabel) return;
    this.isLabelVisible = _showLabel;
  }

  @Input() set showSetting(_showSetting) {
    //true if we need to show the setting and false if we don't need to show the setting just show label for example with criteria form
    if (!_showSetting) return;
    this.isSettingVisible = _showSetting;
  }

  @Input() showRequired: boolean = true;
  @Input() customClass
  @Input() size: 'default' | 'compact' | 'xs' = 'default'; // New input for size control

  fieldName: string;
  isLabelVisible: boolean;
  isSettingVisible: boolean;
  systemMenuId: string;
  fieldNameTranslation: string;
  fieldData: FieldDictionaryDto;
  currentLang: string;
  isRequired: boolean = false;
  // linkedForm = 'https://fonts.google.com/icons'
  // linkedForm ='/admin/WFDef/8ca3c163-1fc9-4f3c-b4f2-aaff5008ec3a'
  linkedForm: string
  private destroy$ = new Subject<void>(); // To manage unsubscription

  get allClasses() {
    return {
      'm-r-10': true,
      'text-capitalize': true,
      'mat-form-field-compact': this.size === 'compact',
      'mat-form-field-xs': this.size === 'xs',
      ...this.customClass
    };
  }

  constructor(
    public dialog: MatDialog,
    private _router: Router,
    private activatedRoute: ActivatedRoute,
    private translate: TranslateService,
    private baseService: BaseService,
    private themeLayout: ThemeLayoutService,
    private fieldDictionaryListService: FieldDictionaryListService
  ) { }

  ngOnChanges(changes: SimpleChanges) {
    // console.log('changes',changes)
    if (changes['showRequired']) this.isRequired = changes['showRequired'].currentValue
  }

  ngOnInit(): void {
    // Subscribe to route params
    this.activatedRoute.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.systemMenuId = params['id'];
    });

    // Subscribe to theme layout direction changes
    this.themeLayout.directionChange.pipe(takeUntil(this.destroy$)).subscribe((dir) => {
      this.currentLang = dir === RTL ? 'ar' : 'en';
      this.getFieldData();
    });

    // Set the initial language and fetch field data
    this.currentLang = this.baseService.getCurrentLanguage();
    this.getFieldData();
  }

  getFieldData() {
    this.fieldDictionaryListService
      .getList()
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        const pageData = res.find((x) => x.pageid == this.systemMenuId);
        // debugger
        if (pageData) {
          // console.log('pageData from label', pageData);
          const pageDataList = pageData.list.map(item => ({
            ...item,
            fieldname: item.fieldname.includes('.')
              ? item.fieldname.split('.').pop()
              : item.fieldname  // remove table name . from fieldname
          }));
          this.fieldData = pageDataList.find((x) => x.fieldname.toLowerCase() == this.fieldName.toLowerCase());
          this.isRequired = this.fieldData?.required;
          this.linkedForm = this.fieldData?.linkedform;
        }
        this.fieldNameTranslation = this.currentLang === 'ar' ?
          this.fieldData?.langvalue.find(x => x.culturename == 'ar-EG')?.value
          : this.fieldData?.langvalue.find(x => x.culturename == this.currentLang)?.value;

        if (!this.fieldNameTranslation) {
          this.fieldNameTranslation = this.translate.instant(this.fieldName);
        }
      });
  }

  showSettings() {
    this.dialog.open(BaseFieldConfigComponent, {
      width: '800px',
      data: { fieldName: this.fieldName, systemMenuId: this.systemMenuId, fielddata: this.fieldData },
      direction: this.baseService.getDirection()
    });
  }

  openInNewTab(event: MouseEvent): void {
    if (this.isExternal(this.linkedForm)) return;

    event.preventDefault();
    const url = this._router.serializeUrl(
      this._router.createUrlTree([this.linkedForm])
    );
    const fullUrl = window.location.origin + url;
    window.open(fullUrl, '_blank');
  }

  isExternal(link: string): boolean {
    return /^(http|https|mailto|tel):/.test(link);
  } // Check if the link is external or angular route

  ngOnDestroy(): void {
    // Emit to complete all active subscriptions
    this.destroy$.next();
    this.destroy$.complete();
  }
}
